import { useEffect, useState } from "react";
import { IFFM, IPayment } from "../../../globals/interfaces";
import moment from "moment";
import { diffDays } from "../../../utils";
import useAltStorage from "./useAltStorage";
import { IscData } from "../../../components/counter/import/interfaces";
import _ from "lodash";

interface ImportCost {
    s_flight_id: string;
    d_storage_start: Date;
    storageDays: number;
    dailyStorage: number;
    totalStorage: number;
    f_weight: number;
}

export default function useImportCalc2(
    ffms: Array<IFFM>,
    payments: Array<IPayment>,
    iscData: IscData
) {

    const { altStorage } = useAltStorage(payments);
    const [importCosts, setImportCosts] = useState<Array<ImportCost>>([]);

    const useCurrentDate = moment().format('YYYY-MM-DD');

    useEffect(() => {
        const importCostArray: Array<ImportCost> = [];

        const ffmsMap: Record<string, IFFM> = {};
        for (const ffm of ffms) {
            if (!ffmsMap[ffm.s_flight_id]) {
                ffmsMap[ffm.s_flight_id] = ffm;
            } else {
                ffmsMap[ffm.s_flight_id].f_weight += ffm.f_weight;
            }
        }

        for (const key in ffmsMap) {
            const ffm = ffmsMap[key];
            const { s_flight_id, d_storage_start, f_weight } = ffm;
            const storageStartDate = moment
                .utc(ffm.d_storage_start)
                .format('YYYY-MM-DD');

            let storageDays = diffDays(storageStartDate, useCurrentDate);

            const { useImportPerKg, useImportMinCharge } = getMinCharges(iscData, d_storage_start);

            const dailyStorage = Number(Math.max(f_weight * useImportPerKg, useImportMinCharge).toFixed(2));
            
            // Used to determine if any manual STORAGE charges were added for s_mawb/s_flight_id
            const altStorageExists = altStorage[s_flight_id];

            // If altStorageExists use that otherwise use the regular storage calc
            const totalStorage: number = altStorageExists
                ? altStorageExists.altStorageAmount
                : Math.max(dailyStorage * storageDays, 0);

            importCostArray.push({
                s_flight_id,
                d_storage_start,
                storageDays,
                dailyStorage,
                totalStorage,
                f_weight
            });
        }

        setImportCosts(importCostArray);
    }, [ffms, altStorage]);

    return {
        importCosts
    };
}

const getMinCharges = (iscData: IscData, d_arrival_date: Date) => {

    const airlineDataWithDetail = _.get(iscData, 'airlineDataWithDetail', {});
    const corpStationData = _.get(iscData, 'corpStationData', {});
    const d_switch = _.get(airlineDataWithDetail, 'AirlineMappingDetail.d_switch', null);
    
    let useImportPerKg, useImportMinCharge;

    if (moment(d_arrival_date).isSameOrBefore(moment(d_switch))) {
        useImportPerKg = _.get(airlineDataWithDetail, 'AirlineMappingDetail.f_import_per_kg_previous', null);
        useImportMinCharge = _.get(airlineDataWithDetail, 'AirlineMappingDetail.f_import_min_charge_previous', null)
    } else {
        useImportPerKg = _.get(airlineDataWithDetail, 'AirlineMappingDetail.f_import_per_kg', null);
        useImportMinCharge = _.get(airlineDataWithDetail, 'AirlineMappingDetail.f_import_min_charge', null)
    }

    if (useImportPerKg === null) {
        useImportPerKg = _.get(corpStationData, 'f_import_per_kg', 0);
    }

    if (useImportMinCharge === null) {
        useImportMinCharge = _.get(corpStationData, 'f_import_min_charge', 0)
    }

    return {
        useImportPerKg, 
        useImportMinCharge
    }
}